import {
  combineReducers,
  configureStore,
  UnknownAction,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import appSettingsSlice from './app_settings/appSettingsSlice'
import formEditSlice from './applications/applicationStatusSlice.ts'
import familyApplicationReducer from './applications/familyApplicationSlice'
import marketplaceCartReducer from './applications/marketplaceCartSlice'
import paymentsAppealsReducer from './applications/paymentAppealSlice.ts'
import providerPayReducer from './applications/providerPaySlice'
import reimubrusementAccountReducer from './applications/reimubrusementAccountSlice'
import reimbursementReducer from './applications/reimubrusementSlice'
import maintenanceReducer from './maintenance/maintenanceSlice'
import programSettingsSlice from './program_settings/programSettingsSlice.ts'
import AuthSessionReducer from './user/authSessionSlice.ts'
import resetPasswordEmailSlice from './user/resetPasswordEmailSlice.ts'

const persistConfig = {
  key: 'root',
  version: 2, //Increment when you change the structure of a current slice
  storage,
  transforms: [],
  blacklist: ["ProviderPayApplication"],
  whitelist: [
    'AuthSession',
    'FamilyApplication',
    'ResetPasswordEmail',
    'ProgramSettings',
    'AppSettings',
    'Maintenance',
    'ReimbursementApplication',
    'MarketplaceCart',
    'ReimbursementAccount',
    'paymentsAppealsApplication',
  ],
}
const rootReducer = combineReducers({
  AuthSession: AuthSessionReducer,
  FamilyApplication: familyApplicationReducer,
  ResetPasswordEmail: resetPasswordEmailSlice,
  ProgramSettings: programSettingsSlice,
  AppSettings: appSettingsSlice,
  applicationStatus: formEditSlice,
  Maintenance: maintenanceReducer,
  ProviderPayApplication: providerPayReducer,
  ReimbursementApplication: reimbursementReducer,
  MarketplaceCart: marketplaceCartReducer,
  ReimbursementAccount: reimubrusementAccountReducer,
  paymentsAppealsApplication: paymentsAppealsReducer,
})

const sentryReduxEnhancer = Sentry.createReduxEnhancer({})

type Reducers = ReturnType<typeof rootReducer>

export const RESET_STATE = 'RESET_STATE'

const rootReducerWithReset = (
  state: Reducers | undefined,
  action: UnknownAction
) => {
  if (action.type === RESET_STATE) {
    state = undefined
    storage.removeItem('persist:root')
  }
  return rootReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducerWithReset)

export const store = configureStore({
  reducer: persistedReducer,
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer)
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
})

export const purgeStore = () => {
  store.dispatch({ type: RESET_STATE })
  sessionStorage.clear()
}

// export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
